import { createAction, createSelector } from "@reduxjs/toolkit"
import z from "zod"
import { RenderSliceSettings, safeParseJson, SettingId } from "@fusion-advantage/backcompat"
import { DefaultRenderSliceSettings } from "@fusion-advantage/formrender"

export const settingShouldPersistToLocalStorage = (id: SettingId): boolean => {
	switch(id) {
		case SettingId.BearerToken:
			return false
	}
	return true
}

export const ZEphemeralBearerToken = z.object({
	token: z.string(),
	expiredAt: z.number(),
})
export type EphemeralBearerToken = z.infer<typeof ZEphemeralBearerToken>

export interface AppSliceSettings {
	[SettingId.ClientId]: string
	[SettingId.BearerToken]: EphemeralBearerToken|null
	[SettingId.IsTrainingMode]: boolean
	[SettingId.ForceDevRenderer]: boolean
}

// don't use a `true` boolean default in this object;
// flip the phrasing of the name so a false default makes sense
export const DefaultAppSliceSettings: Readonly<AppSliceSettings> = {
	[SettingId.ClientId]: '',
	[SettingId.BearerToken]: null,
	[SettingId.IsTrainingMode]: false,
	[SettingId.ForceDevRenderer]: false,
}

export type AllSettings = {
	app: AppSliceSettings
	render: RenderSliceSettings
}

export type FlatAllSettings = AppSliceSettings&RenderSliceSettings

export interface SettingPersistRow {
	id: SettingId
	value: string|null
}

export const settingPersistRowsToAppSliceSettings = (rows: SettingPersistRow[]): AppSliceSettings => {
	let all: AppSliceSettings = {
		...DefaultAppSliceSettings,
	}
	for(let {id, value} of rows) {
		switch(id) {
			case SettingId.ClientId:
				if(value) {
					all[id] = value
				}
				break
			case SettingId.BearerToken:
				if(!value) {
					continue
				}
				let rParse = safeParseJson(value, ZEphemeralBearerToken)
				// TODO error handling could be improved?
				// to not fill in value, but still warn the log somehow...
				if(rParse.isOk()) {
					all[SettingId.BearerToken] = rParse.value
				}
				break
			case SettingId.ForceDevRenderer:
			case SettingId.IsTrainingMode:
				all[id] = (value === 'true')
				break
		}
	}
	return all
}

export const settingPersistRowsToRenderSliceSettings = (rows: SettingPersistRow[]): RenderSliceSettings => {
	let all: RenderSliceSettings = {
		...DefaultRenderSliceSettings,
	}
	for(let {id, value} of rows) {
		switch(id) {
			case SettingId.ShowDebugInfo:
				all[id] = (value === 'true')
				break
		}
	}
	return all
}

export const restoreAppSliceSettings = createAction<AppSliceSettings>('restoreAppSliceSettings')
export const restoreRenderSliceSettings = createAction<RenderSliceSettings>('restoreRenderSliceSettings')

// this gets memoized
// so we can do a === comparison in storeSettingsMw
export const selectStateFlatAllSettings = createSelector(
	(state: AllSettings) => state.app[SettingId.ClientId],
	(state: AllSettings) => state.app[SettingId.BearerToken],
	(state: AllSettings) => state.app[SettingId.ForceDevRenderer],
	(state: AllSettings) => state.app[SettingId.IsTrainingMode],
	(state: AllSettings) => state.render[SettingId.ShowDebugInfo],
	(clientId, bearerToken, forceDevRenderer, isTrainingMode, showDebugInfo): FlatAllSettings => {
		return {
			clientId,
			bearerToken,
			forceDevRenderer,
			isTrainingMode,
			showDebugInfo,
		}
	}
)

export const settingStateToUpdatePersistRows = (prevState: FlatAllSettings, nextState: FlatAllSettings): SettingPersistRow[] => {
	let rows: SettingPersistRow[] = []
	for(let id of Object.values(SettingId)) {
		if(prevState[id] === nextState[id]) {
			continue
		}
		switch(id) {
			case SettingId.ClientId:
				rows.push({
					id,
					value: nextState[id],
				})
				break
			case SettingId.BearerToken:
				let value: string|null = null
				if(nextState[id]) {
					// TODO error handling could be improved?
					value = JSON.stringify(nextState[id])
				}
				rows.push({id, value})
				break
			case SettingId.ForceDevRenderer:
			case SettingId.IsTrainingMode:
			case SettingId.ShowDebugInfo:
				rows.push({
					id,
					value: nextState[id] ? 'true' : 'false',
				})
				break
		}
	}
	return rows
}
